<div class="content-wrapper">
  <div class="notifications-container">
    <app-title-bar
      title="Notifications"
      description="Track license plate captures and order events"
      [showButton]="false"
    >
    </app-title-bar>

    <!-- Search and Filter Section -->
    <div class="search-filter-section">
      <app-searchbar
        placeholder="Search by license plates"
        [searchTerm]="searchTerm"
        (searchChange)="onSearchChange($event)"
        iconSize="small"
      >
      </app-searchbar>

      <div class="table-filters">
        <!-- Location Filter (Admin Only) -->
        <div *ngIf="isAdmin && availableLocations.length > 0" class="location-filter">
          <label for="location-select">Location:</label>
          <div class="location-filter__select">
            <mat-select
              id="location-select"
              class="form-select"
              [(ngModel)]="selectedLocationId"
              placeholder="Select Location"
              panelClass="notifications-location-select-panel"
              disableOptionCentering
              (selectionChange)="onLocationChange()"
            >
              <mat-option
                *ngFor="let availableLocation of availableLocations"
                [value]="availableLocation.id"
                >{{ availableLocation.name }}</mat-option
              >
            </mat-select>
          </div>
        </div>

        <app-simple-dropdown
          [options]="laneFilterOptions"
          *ngIf="showLaneFilter"
          [selectedValue]="selectedLaneFilterId"
          [placeholder]="'All Lanes'"
          [title]="'Select Lane'"
          [isOpen]="isLaneDropdownOpen"
          (selectionChange)="selectLaneFilter($event)"
          (toggle)="toggleLaneDropdown()"
          (close)="closeLaneDropdown()"
        >
        </app-simple-dropdown>

        <!-- Event filter (CWS-2051): hide the checked events -->
        <app-action-button
          buttonText="Filter"
          iconSrc="assets/images/package/adjustments.svg"
          [showCount]="true"
          [count]="activeEventFilterCount"
          (buttonClick)="openEventFilterModal()"
        >
        </app-action-button>
      </div>
    </div>

    <!-- Loading State (initial load or first fetch with no rows yet) -->
    <div
      *ngIf="!hasInitialLoadCompleted || (isLoading && notifications.length === 0)"
      class="loading-container"
    >
      <div class="loader"></div>
      <p>Loading notifications...</p>
    </div>

    <!-- Empty State — only after the first API response -->
    <div *ngIf="hasInitialLoadCompleted && !isLoading && totalRecords === 0" class="empty-state">
      <p *ngIf="hasActiveSearch; else noNotifications">No notifications match "{{ searchTerm }}"</p>
      <ng-template #noNotifications><p>No notifications found</p></ng-template>
    </div>

    <!-- Notifications List -->
    <div
      *ngIf="hasInitialLoadCompleted && totalRecords > 0"
      class="notifications-list-container"
      [class.is-refreshing]="isLoading"
    >
      <div class="notifications-table">
        <div class="table-header">
          <div class="header-cell timestamp">Date & Time</div>
          <div class="header-cell event">Event</div>
          <div class="header-cell lane">Lane</div>
          <div class="header-cell state">State</div>
          <div class="header-cell license-plate">Plate</div>
          <div class="header-cell image">Image</div>
          <div class="header-cell vehicle">Vehicle</div>
          <div class="header-cell make">Make</div>
          <div class="header-cell model">Model</div>
        </div>
        <div class="table-body">
          <div
            *ngFor="let row of notificationRows; trackBy: trackByIndex"
            class="table-row"
            (click)="onNotificationClick(row.notification)"
          >
            <div class="cell timestamp">
              <time class="timestamp-value">{{ row.timestamp | notificationTimestamp }}</time>
            </div>
            <div class="cell event">
              <span class="event-badge">{{ row.displayEvent }}</span>
            </div>
            <div class="cell lane">
              <span class="lane-value">{{ row.laneName || '—' }}</span>
            </div>
            <div class="cell state">
              <span class="state-value">{{ row.state || '—' }}</span>
            </div>
            <div class="cell license-plate">
              <span class="license-plate-value">{{ row.plate }}</span>
            </div>
            <div class="cell image">
              <button
                *ngIf="row.plateImageUrl as plateImage; else noImage"
                type="button"
                class="plate-image-button"
                title="View vehicle image"
                (click)="openImageOverlay(row, $event)"
              >
                <img
                  class="plate-image"
                  [src]="plateImage"
                  [alt]="'License plate ' + row.plate"
                  loading="lazy"
                  decoding="async"
                  referrerpolicy="no-referrer"
                />
              </button>
              <ng-template #noImage>
                <span class="image-placeholder" aria-label="No image available">—</span>
              </ng-template>
            </div>
            <div class="cell vehicle">
              <button
                *ngIf="row.vehicleImageUrl as vehicleImage; else noVehicleImage"
                type="button"
                class="vehicle-image-button"
                title="View vehicle image"
                (click)="openImageOverlay(row, $event)"
              >
                <img
                  class="vehicle-image"
                  [src]="vehicleImage"
                  [alt]="'Vehicle ' + row.plate"
                  loading="lazy"
                  decoding="async"
                  referrerpolicy="no-referrer"
                />
              </button>
              <ng-template #noVehicleImage>
                <span class="image-placeholder" aria-label="No vehicle image available">—</span>
              </ng-template>
            </div>
            <div class="cell make">
              <span class="make-value">{{ row.make || '—' }}</span>
            </div>
            <div class="cell model">
              <span class="model-value">{{ row.model || '—' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <app-pagination-bar
        class="notifications-pagination"
        testId="notifications-pagination"
        [paginationData]="paginationData"
        [itemsPerPageOptions]="rowsPerPageOptions"
        (pageChange)="onPageChange($event)"
        (itemsPerPageChange)="onRowsPerPageChange($event)"
      >
      </app-pagination-bar>
    </div>
  </div>
</div>

<!-- Event Filter Modal (CWS-2051) -->
<app-filter-modal
  [isVisible]="isEventFilterModalVisible"
  [categories]="eventFilterCategories"
  header="Filter Notifications"
  primaryButtonText="Apply Filters"
  secondaryButtonText="Clear Filters"
  (close)="closeEventFilterModal()"
  (apply)="onEventFilterApplied($event)"
>
</app-filter-modal>

<!-- Vehicle View Overlay (license plate image click) -->
<div *ngIf="isImageOverlayOpen" class="image-overlay" (click)="closeImageOverlay()">
  <div class="image-overlay__content" (click)="$event.stopPropagation()">
    <div class="image-overlay__header">
      <span class="image-overlay__title">{{ overlayPlate || 'Vehicle view' }}</span>
      <button
        type="button"
        class="image-overlay__close"
        aria-label="Close"
        (click)="closeImageOverlay()"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 5L5 15M5 5L15 15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <img
      *ngIf="overlayImageUrl"
      class="image-overlay__image"
      [src]="overlayImageUrl"
      [alt]="'Vehicle view for ' + overlayPlate"
      referrerpolicy="no-referrer"
    />
  </div>
</div>
